'use client'

import { Annotation, Annotation2 } from '@/types/annotation'

// --------------

interface EventResult {
  name: string
  date: string
  venue: string
  url: string
  source: string
  snippet: string
  category?: string
}

// --------------

interface EventDiscoveryResult {
  events: EventResult[]
  query: string
}

// --------------

const CATEGORY_COLORS: Record<string, string> = {
  music: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  concert: 'bg-purple-500/15 text-purple-600 dark:text-purple-400',
  festival: 'bg-orange-500/15 text-orange-600 dark:text-orange-400',
  sports: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  comedy: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  theater: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
  theatre: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
  art: 'bg-indigo-500/15 text-indigo-600 dark:text-indigo-400',
  food: 'bg-red-500/15 text-red-600 dark:text-red-400',
  market: 'bg-teal-500/15 text-teal-600 dark:text-teal-400',
  default: 'bg-blue-500/15 text-blue-600 dark:text-blue-400'
}

// --------------

function getCategoryColor(category?: string): string {
  if (!category) return CATEGORY_COLORS.default
  const lower = category.toLowerCase()
  for (const [key, value] of Object.entries(CATEGORY_COLORS)) {
    if (lower.includes(key)) return value
  }
  return CATEGORY_COLORS.default
}

// --------------

function truncateSnippet(snippet: string, maxLen = 80): string {
  if (!snippet || snippet.length <= maxLen) return snippet
  return snippet.slice(0, maxLen).trimEnd() + '…'
}

// --------------

function EventCard({ event }: { event: EventResult }) {
  const categoryColor = getCategoryColor(event.category)

  return (
    <a
      href={event.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`View ${event.name}`}
      className="border-border/40 bg-card/50 hover:border-primary/30 group flex w-64 shrink-0 flex-col overflow-hidden rounded-xl border backdrop-blur-sm transition-all hover:shadow-md"
    >
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        {/* Category badge */}
        {event.category && (
          <span
            className={`w-fit rounded-full px-2 py-0.5 text-[10px] font-medium ${categoryColor}`}
          >
            {event.category}
          </span>
        )}

        {/* Event name */}
        <p className="text-foreground line-clamp-2 text-sm font-semibold leading-tight">
          {event.name}
        </p>

        {/* Date */}
        {event.date && (
          <p className="text-muted-foreground flex items-center gap-1 text-xs">
            <span>📅</span>
            <span>{event.date}</span>
          </p>
        )}

        {/* Venue / source */}
        {(event.venue || event.source) && (
          <p className="text-muted-foreground flex items-center gap-1 text-xs">
            <span>📍</span>
            <span>{event.venue || event.source}</span>
          </p>
        )}

        {/* Snippet */}
        {event.snippet && (
          <p className="text-muted-foreground mt-0.5 text-[11px] leading-snug">
            {truncateSnippet(event.snippet)}
          </p>
        )}

        {/* Source link domain */}
        {event.source && event.venue && (
          <p className="text-muted-foreground/70 mt-auto pt-1 text-[10px]">
            {event.source}
          </p>
        )}
      </div>
    </a>
  )
}

// --------------

export const EventDiscoveryAnnotation: React.FC<{
  annotation: Annotation | Annotation2
}> = ({ annotation }) => {
  const result = (annotation as Record<string, unknown>)['eventDiscovery__searchLocalEvents'] as
    Record<string, unknown> | undefined
  if (!result) return null

  const data: EventDiscoveryResult =
    ('result' in result ? result.result : result) as EventDiscoveryResult

  if (!data?.events?.length) return null

  return (
    <div className="mt-3 space-y-2">
      {/* Header */}
      <div>
        <p className="text-foreground/80 text-sm font-medium">
          🎉 Local events
        </p>
        {data.query && (
          <p className="text-muted-foreground text-xs">
            {data.query}
          </p>
        )}
      </div>

      {/* Scrollable card row */}
      <div
        className="flex gap-3 overflow-x-auto pb-2"
        role="list"
        aria-label="List of local events"
      >
        {data.events.map((event, idx) => (
          <div key={`${event.name}-${idx}`} role="listitem">
            <EventCard event={event} />
          </div>
        ))}
      </div>
    </div>
  )
}
